import { QuotaType } from '@bpinternal/const'
import { useUsage } from './useWorkspaceUsages'
import { LucideIcon, Sparkles, File, MessageCircle, Table, DatabaseIcon } from 'lucide-react'
import { QUOTA_TYPE_USER_FACING_PROPERTIES_MAP } from '~/features/usage/constants'
import { formatValueWithUnit } from '~/features/usage/helpers'
import { getCurrentLocalDateString } from '~/utils'

export type UsageItem = {
  current: number
  max: number
  label: string
  icon: LucideIcon
  type: QuotaType
  percentage: number
  description: string
}

const USAGE_ORDER: QuotaType[] = [
  'ai_spend',
  'storage_count',
  'invocation_calls',
  'table_row_count',
  'knowledgebase_vector_storage',
]

const QUOTA_ICONS: Partial<Record<QuotaType, LucideIcon>> = {
  ai_spend: Sparkles,
  storage_count: File,
  invocation_calls: MessageCircle,
  table_row_count: Table,
  knowledgebase_vector_storage: DatabaseIcon,
}

export const useUsageItems = (workspaceId: string): UsageItem[] => {
  const currentPeriod = getCurrentLocalDateString()
  const usagesRecord = useUsage({
    workspaceId,
    quotas: USAGE_ORDER,
    period: currentPeriod,
  })

  const usageItems = USAGE_ORDER.map((type) => {
    const usage = usagesRecord[type]
    if (!usage) return null

    const Icon = QUOTA_ICONS[type] || MessageCircle
    const quota = QUOTA_TYPE_USER_FACING_PROPERTIES_MAP[type]
    const label = quota?.shortName || quota?.name || type

    const descriptor = type === 'invocation_calls' ? ' calls' : type === 'table_row_count' ? ' rows' : ''
    const description = `${formatValueWithUnit(usage.value, type)} of ${formatValueWithUnit(usage.quota || 1, type)}${descriptor} used`

    return {
      current: usage.value,
      max: usage.quota || 1,
      label,
      icon: Icon,
      type,
      percentage: (usage.value / (usage.quota || 1)) * 100,
      description,
    }
  }).filter((usage) => usage !== null)

  return usageItems
}
